import { type UserFileRead } from '@/uppy-file-upload/schemas'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { MedSummaryFileStatus } from '@/medical-billing-summaries/asset/components/add-files/med-summary-file-status'

export type MedSummaryAssetFileCardState = {
  fileName: string
  status: Exclude<MedSummaryFileStatus, MedSummaryFileStatus.EXTRACTED>
  errorMessage: string
  ready: boolean
}

export function isActiveMedSummaryAssetUpload(uploadState?: FileUploadState): uploadState is FileUploadState {
  return !!uploadState && ['validating', 'queued', 'initializing', 'uploading'].includes(uploadState.client.uploadStatus)
}

/**
 * Card state for the asset Add Files staging list. The asset flow submits only COMPLETED files;
 * initialize/apply reject unprocessed files, and preview-add reports them NOT_READY.
 */
export function getMedSummaryAssetFileCardState(params: {
  fileId?: string
  file?: UserFileRead
  uploadState?: FileUploadState
  loadError?: boolean
}): MedSummaryAssetFileCardState {
  const { fileId, file, uploadState, loadError } = params
  const clientStatus = uploadState?.client.uploadStatus
  const record = uploadState?.backend?.record ?? file
  const fileName = record?.original_filename ?? uploadState?.client.filename ?? fileId ?? 'File'

  if (clientStatus === 'unsupported_type') {
    return { fileName, status: MedSummaryFileStatus.ERROR, errorMessage: 'Unsupported file type', ready: false }
  }

  if (clientStatus === 'error') {
    return { fileName, status: MedSummaryFileStatus.ERROR, errorMessage: uploadState?.client.uploadError ?? 'Upload failed', ready: false }
  }

  if (loadError && !record) {
    return { fileName, status: MedSummaryFileStatus.ERROR, errorMessage: 'Could not load file', ready: false }
  }

  if (isActiveMedSummaryAssetUpload(uploadState)) {
    return { fileName, status: MedSummaryFileStatus.UPLOADING, errorMessage: '', ready: false }
  }

  if (!record) {
    return { fileName, status: MedSummaryFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Failed) {
    return { fileName, status: MedSummaryFileStatus.ERROR, errorMessage: record.processing_error ?? 'Processing failed', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Pending || record.processing_status === UserFileProcessingStatus.Processing) {
    return { fileName, status: MedSummaryFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  return { fileName, status: MedSummaryFileStatus.COMPLETED, errorMessage: '', ready: true }
}
